"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useIdentity } from "@/lib/providers/identity";
import { billingWallAppliesAt, billingWallPolicyOf, DEFAULT_PLATFORM_SCOPE, scopeForPath, settingsPathOf } from "@/domain/scope/platform-scope";
import { isSetupDue } from "@/entities/billing/model/setup-gate";
import { useSetupGate } from "@/entities/billing/model/use-setup-gate";
import { billingBannerRegionOf } from "@/entities/billing/model/billing-banner-region";
import { paymentWallRouteOf } from "@/entities/billing/model/payment-wall-route";
import { useSuperAdminBillingGateExempt } from "@/entities/billing/model/use-super-admin-billing-gate-exempt";
import { BillingGateHolds, drawsBeforeTheGate, rememberTakeover, rememberedTakeover, sectionOfLocation } from "@/entities/billing/model/draws-before-the-gate";
import { AppLoading } from "@/components/ui/app-loading";
import { usePublishBannerHeight } from "@/shared/ui/use-publish-banner-height";
import { BillingGateBanners } from "./billing-gate-banners";
import { HardLockPage } from "./hard-lock-page";
import { BillingPendingPage } from "./billing-pending-page";
import { SetupFloorGate } from "./setup-floor-gate";
import { SetupPendingPage } from "./setup-pending-page";
import { ScopePaymentWallSlot } from "@/entities/billing/model/scope-payment-wall-slot";
import { TrialEndedPendingPage } from "./trial-ended-pending-page";
const EXEMPT_PATHS = ["/settings/billing", "/payment", "/payment/return", "/sign-in", "/sign-up", "/inactive", "/trial-end", "/compare-plans"];
function isExemptPath(pathname: string): boolean {
  return EXEMPT_PATHS.some(exempt => pathname === exempt || pathname.startsWith(`${exempt}/`));
}
interface BillingGateProps {
  children: React.ReactNode;
}
export function BillingGate(t0) {
  const $ = _c(10);
  const {
    children
  } = t0;
  const bannerRef = usePublishBannerHeight("billing");
  const router = useRouter();
  const pathname = usePathname();
  const renderScopePaymentWall = useContext(ScopePaymentWallSlot);
  const {
    identity,
    identityScope,
    isLoading: identityLoading
  } = useIdentity();
  const isSuperAdmin = !identityLoading && identity?.isSuperAdmin === true;
  const isAdmin = !identityLoading && identity !== null && identity.isAdmin === true;
  const surfaceScope = scopeForPath(pathname);
  const productRungWall = billingWallPolicyOf(surfaceScope) === "paid-rungs";
  const productWallTarget = `${settingsPathOf(surfaceScope)}/billing`;
  const isExempt = isExemptPath(pathname) || pathname.startsWith("/super-admin") || !billingWallAppliesAt(pathname) || identityScope === DEFAULT_PLATFORM_SCOPE && identity !== null && !identity.heldProducts.includes(DEFAULT_PLATFORM_SCOPE);
  const drawsEarly = drawsBeforeTheGate(pathname, sectionOfLocation()) && !rememberedTakeover(identity?.orgId);
  const {
    skipBillingGates,
    hasHydrated: skipHydrated
  } = useSuperAdminBillingGateExempt();
  const skipGates = isSuperAdmin && skipBillingGates;
  const holdWallRedirects = isSuperAdmin && !skipHydrated;
  const {
    setupFloor,
    setupDecision,
    gateStatus,
    gateLoading
  } = useSetupGate({
    pathname,
    surfaceScope,
    isExempt,
    signedIn: !identityLoading && identity !== null,
    isAdmin,
    skipGates,
    holdWallRedirects
  });
  let t1;
  if ($[0] !== gateStatus || $[1] !== productRungWall) {
    t1 = gateStatus?.requiresPayment === true && !isSetupDue(gateStatus) && !productRungWall && (gateStatus?.effectiveRestrictions ?? gateStatus?.decision?.restrictions)?.wall !== "inactive_redirect";
    $[0] = gateStatus;
    $[1] = productRungWall;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const requiresPayment = t1;
  const paymentWallRoute = paymentWallRouteOf({
    surfaceScope,
    wallPolicy: billingWallPolicyOf(surfaceScope),
    requiresPayment,
    paymentStatus: null,
    isAdmin
  });
  const effectiveRestrictions = gateStatus?.effectiveRestrictions ?? gateStatus?.decision?.restrictions;
  const wall = effectiveRestrictions?.wall ?? "none";
  const banners = effectiveRestrictions?.banners ?? [];
  const trialEndedWall = wall === "trial_ended" && !productRungWall;
  const productWallUp = productRungWall && wall !== "none";
  const orgIdForMemory = identity?.orgId ?? null;
  const tookOver = setupDecision === "redirect" || setupDecision === "pending" || paymentWallRoute === "redirect" || paymentWallRoute === "pending-page" || trialEndedWall || wall === "inactive_redirect" || wall === "hard_lock_takeover";
  const answered = !gateLoading && gateStatus !== undefined && gateStatus !== null && !isExempt && !skipGates;
  let t2;
  let t3;
  if ($[3] !== answered || $[4] !== orgIdForMemory || $[5] !== tookOver) {
    t2 = () => {
      if (answered && orgIdForMemory !== null) {
        rememberTakeover(orgIdForMemory, tookOver);
      }
    };
    t3 = [answered, orgIdForMemory, tookOver];
    $[3] = answered;
    $[4] = orgIdForMemory;
    $[5] = tookOver;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  useEffect(() => {
    if (paymentWallRoute === "redirect" && !skipGates && !holdWallRedirects && !isExempt) {
      router.push("/payment");
    }
  }, [paymentWallRoute, skipGates, holdWallRedirects, isExempt, router]);
  useEffect(() => {
    if (isAdmin && !skipGates && !holdWallRedirects && !isExempt && trialEndedWall) {
      router.push("/trial-end");
    }
  }, [isAdmin, skipGates, holdWallRedirects, isExempt, trialEndedWall, router]);
  useEffect(() => {
    if (isAdmin && !skipGates && !holdWallRedirects && !isExempt && productWallUp && setupDecision === "pass") {
      router.push(`${productWallTarget}?walled=${wall}`);
    }
  }, [isAdmin, skipGates, holdWallRedirects, isExempt, productWallUp, productWallTarget, wall, setupDecision, router]);
  if (isExempt && !setupFloor) {
    return <>{children}</>;
  }
  if (setupFloor) {
    const unresolved = identityLoading || !identity || isSuperAdmin && !skipHydrated || skipGates || gateLoading || !gateStatus;
    return <SetupFloorGate unresolved={unresolved} decision={setupDecision} scope={surfaceScope}>{children}</SetupFloorGate>;
  }
  if (identityLoading) {
    return <AppLoading />;
  }
  if (!identity) {
    return <>{children}</>;
  }
  if (isSuperAdmin && !skipHydrated) {
    return <>{children}</>;
  }
  if (skipGates) {
    return <>{children}</>;
  }
  if (gateLoading) {
    return drawsEarly ? <>{null}<BillingGateHolds.Provider value={true}>{children}</BillingGateHolds.Provider></> : <AppLoading />;
  }
  if (!gateStatus) {
    let t4;
    if ($[8] !== children) {
      t4 = <BillingGateHolds.Provider value={false}>{children}</BillingGateHolds.Provider>;
      $[8] = children;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    return <>{null}{t4}</>;
  }
  if (setupDecision === "redirect") {
    return <AppLoading message="Redirecting..." />;
  }
  if (setupDecision === "pending") {
    return <SetupPendingPage scope={surfaceScope} />;
  }
  if (productRungWall) {
    return productWallUp && isAdmin ? <AppLoading message="Redirecting..." /> : <>{children}</>;
  }
  if (paymentWallRoute === "redirect") {
    return <AppLoading message="Redirecting..." />;
  }
  if (paymentWallRoute === "pending-page") {
    return <BillingPendingPage />;
  }
  const knownStatus = gateStatus;
  const renderRestrictions = () => {
    if (trialEndedWall) {
      if (isAdmin) {
        return <AppLoading message="Redirecting..." />;
      }
      return <TrialEndedPendingPage billingAdminName={knownStatus.billingAdminName} />;
    }
    if (wall === "inactive_redirect") {
      router.push("/inactive");
      return <AppLoading message="Redirecting..." />;
    }
    if (wall === "hard_lock_takeover") {
      return <HardLockPage />;
    }
    const bannerRegion = billingBannerRegionOf({
      gateStatus: knownStatus,
      banners,
      isAdmin,
      surfaceScope
    });
    return <>{bannerRegion === null ? null : <div key={bannerRegion.kind} ref={bannerRef} className="flow-root"><BillingGateBanners region={bannerRegion} gateStatus={knownStatus} isAdmin={isAdmin} /></div>}<BillingGateHolds.Provider value={false}>{children}</BillingGateHolds.Provider></>;
  };
  const scopePayToEnter = surfaceScope !== DEFAULT_PLATFORM_SCOPE && billingWallPolicyOf(surfaceScope) === "all-surfaces" && !isSetupDue(gateStatus) && wall !== "inactive_redirect";
  if (scopePayToEnter && renderScopePaymentWall !== null) {
    return renderScopePaymentWall({
      surfaceScope,
      isAdmin,
      children: renderRestrictions()
    });
  }
  return renderRestrictions();
}